import type { UserOrGroup } from 'src/schemas/global';

export const ownerAndContributorIds = (data: {
  Owners: UserOrGroup[] | null;
  Contributors: UserOrGroup[] | null;
}) => ({
  ...contributorIds(data),
  ...ownerIds(data),
});

/**
 * RSP-4302 — Same shape as ownerAndContributorIds but also spreads viewerIds.
 * Use this on any entity whose form schema has a Viewers field. The destination
 * mutation MUST accept ViewerUserIds / ViewerGroupIds — Hasura strict-validates
 * inputs and unknown fields trigger "field 'ViewerUserIds' not found in type"
 * errors that surface as "Failed to save changes" in the UI. Currently the
 * Insert/Update inputs for Risk, Assessment (incl. CMA + IAR), Control,
 * Document, Indicator, Obligation, Issue, QuestionnaireTemplate, ThirdParty,
 * and InternalAudit accept these fields, plus the inline-variable Action
 * mutations.
 */
export const ownerContributorAndViewerIds = (data: {
  Owners: UserOrGroup[] | null;
  Contributors: UserOrGroup[] | null;
  Viewers?: UserOrGroup[] | null;
}) => ({
  ...contributorIds(data),
  ...ownerIds(data),
  ...viewerIds(data),
});

export const ownerIds = (data: { Owners: UserOrGroup[] | null }) => ({
  OwnerUserIds:
    data.Owners?.filter((c) => c.type === 'user').map((c) => c.value) ?? [],
  OwnerGroupIds:
    data.Owners?.filter((c) => c.type === 'userGroup').map((c) => c.value) ??
    [],
});

export const contributorIds = (data: {
  Contributors: UserOrGroup[] | null;
}) => ({
  ContributorUserIds:
    data.Contributors?.filter((c) => c.type === 'user').map((c) => c.value) ??
    [],
  ContributorGroupIds:
    data.Contributors?.filter((c) => c.type === 'userGroup').map(
      (c) => c.value
    ) ?? [],
});

export const viewerIds = (data: { Viewers?: UserOrGroup[] | null }) => ({
  ViewerUserIds:
    data.Viewers?.filter((c) => c.type === 'user').map((c) => c.value) ?? [],
  ViewerGroupIds:
    data.Viewers?.filter((c) => c.type === 'userGroup').map((c) => c.value) ??
    [],
});

export type Requiredish<T> = {
  [K in keyof Required<T>]: T[K];
};
